import { useEffect, useState } from 'react'
import { Cookie, ShieldCheck, X } from 'lucide-react'
import {
  readConsent, hasDecided, acceptAll, rejectAll, setConsent, onConsentChange,
  type ConsentState,
} from '@/lib/consent'

interface Props {
  /** Force-show the manager (used by the footer "Cookie settings" link) */
  alwaysOpen?: boolean
  onClose?: () => void
}

export default function CookieConsentBanner({ alwaysOpen = false, onClose }: Props) {
  // Default to false on first render so SSR markup matches client.
  const [visible, setVisible] = useState(alwaysOpen)
  const [manageOpen, setManageOpen] = useState(alwaysOpen)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  useEffect(() => {
    const current = readConsent()
    setAnalytics(current.analytics)
    setMarketing(current.marketing)
    if (alwaysOpen) {
      setVisible(true)
      setManageOpen(true)
    } else {
      setVisible(!hasDecided())
    }
    const off = onConsentChange((next: ConsentState) => {
      setAnalytics(next.analytics)
      setMarketing(next.marketing)
    })
    return () => off()
  }, [alwaysOpen])

  function handleAcceptAll() {
    acceptAll()
    setVisible(false)
    setManageOpen(false)
    onClose?.()
  }
  function handleRejectAll() {
    rejectAll()
    setVisible(false)
    setManageOpen(false)
    onClose?.()
  }
  function handleSavePrefs() {
    setConsent({ analytics, marketing })
    setVisible(false)
    setManageOpen(false)
    onClose?.()
  }

  if (!visible && !alwaysOpen) return null

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      aria-modal={manageOpen}
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        padding: '0.75rem',
      }}
    >
      <div
        style={{
          maxWidth: '880px',
          margin: '0 auto',
          background: '#0d0d0d',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '0.875rem',
          padding: '1.1rem 1.2rem',
          color: '#e2e8f0',
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
        }}
      >
        {!manageOpen ? (
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start', maxWidth: '52rem' }}>
              <Cookie size={20} style={{ flexShrink: 0, color: '#ff6a1a', marginTop: '0.15rem' }} />
              <div>
                <p id="cookie-consent-title" style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                  We use cookies
                </p>
                <p id="cookie-consent-desc" style={{ fontSize: '0.8rem', color: '#cbd5e1', lineHeight: 1.55 }}>
                  Essential cookies keep wavbee working. With your consent, we&apos;ll also use analytics and marketing
                  cookies to improve the site and measure ads. You can change your choice anytime from the footer.{' '}
                  <a href="/privacy-policy" style={{ color: '#ff6a1a', textDecoration: 'underline' }}>Read our privacy policy</a>.
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setManageOpen(true)} className="marketing-button-ghost" style={{ padding: '0.55rem 0.95rem', fontSize: '0.8rem' }}>
                Manage
              </button>
              <button type="button" onClick={handleRejectAll} className="marketing-button-secondary" style={{ padding: '0.55rem 0.95rem', fontSize: '0.8rem' }}>
                Reject all
              </button>
              <button type="button" onClick={handleAcceptAll} className="marketing-button-primary" style={{ padding: '0.55rem 1.1rem', fontSize: '0.85rem' }}>
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
              <div style={{ display: 'flex', gap: '0.55rem', alignItems: 'center' }}>
                <ShieldCheck size={18} style={{ color: '#ff6a1a' }} />
                <h3 style={{ fontWeight: 700, fontSize: '1rem' }}>Cookie preferences</h3>
              </div>
              <button type="button" onClick={() => alwaysOpen ? onClose?.() : setManageOpen(false)} aria-label="Close" style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <CategoryRow
              title="Strictly necessary"
              description="Keep you logged in, store your cart, prevent fraud. Always on — required for the site to work."
              locked
              checked
            />
            <CategoryRow
              title="Analytics"
              description="Helps us understand which pages people use so we can improve the experience. Anonymous and aggregated."
              checked={analytics}
              onChange={setAnalytics}
            />
            <CategoryRow
              title="Marketing"
              description="Lets us measure ad performance on Google and Meta. Used only to attribute applications to the campaign that referred you."
              checked={marketing}
              onChange={setMarketing}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.85rem' }}>
              <button type="button" onClick={handleRejectAll} className="marketing-button-secondary" style={{ padding: '0.55rem 0.95rem', fontSize: '0.8rem' }}>
                Reject all
              </button>
              <button type="button" onClick={handleSavePrefs} className="marketing-button-ghost" style={{ padding: '0.55rem 0.95rem', fontSize: '0.8rem' }}>
                Save preferences
              </button>
              <button type="button" onClick={handleAcceptAll} className="marketing-button-primary" style={{ padding: '0.55rem 1.1rem', fontSize: '0.85rem' }}>
                Accept all
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function CategoryRow({
  title, description, checked, onChange, locked,
}: {
  title: string
  description: string
  checked: boolean
  onChange?: (v: boolean) => void
  locked?: boolean
}) {
  return (
    <label style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '1rem',
      padding: '0.75rem 0',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      cursor: locked ? 'default' : 'pointer',
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{title}</div>
        <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.2rem', lineHeight: 1.5, maxWidth: '36rem' }}>{description}</div>
      </div>
      <span
        aria-hidden
        style={{
          width: '2.5rem', height: '1.4rem', borderRadius: '999px',
          background: checked ? '#ff6a1a' : 'rgba(255,255,255,0.18)',
          opacity: locked ? 0.6 : 1, position: 'relative', flexShrink: 0,
          transition: 'background 0.18s ease',
        }}
      >
        <span style={{
          position: 'absolute', top: '0.15rem', left: checked ? '1.2rem' : '0.15rem',
          width: '1.1rem', height: '1.1rem', borderRadius: '999px', background: '#fff',
          transition: 'left 0.18s ease',
        }} />
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={locked}
        onChange={(e) => onChange?.(e.target.checked)}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
      />
    </label>
  )
}
